import { deviceTokenSchema } from '@rollcall/contracts'

/**
 * Устройство: единственный признак владения до регистрации (D29).
 *
 * Аккаунтов ещё нет — они появятся у мастера в фазе 3 (D8), — а драфт
 * кому-то принадлежать обязан: без владельца ссылка на него открывает
 * персонажа кому угодно. D8 закладывает под гостевой доступ привязку
 * к устройству (`seat.device_token`); здесь та же привязка, только
 * на шаг раньше — у драфта места ещё нет.
 *
 * Чем это не является: аутентификацией. Устройство — не человек;
 * очистка хранилища теряет драфты безвозвратно, а тот, кто прочтёт
 * хранилище браузера, получит и их. Для персонажа без кампании
 * и без личных данных этого достаточно, и на большее оно не претендует.
 *
 * Токен заводится один раз и живёт в локальном хранилище рядом
 * с темой (`theme.ts`). Приватный режим Safari бросает на любом
 * обращении к хранилищу — тогда токен остаётся только в памяти
 * вкладки: драфт создастся и откроется, но не переживёт перезагрузку.
 */

export const DEVICE_STORAGE_KEY = 'rollcall.device'

/** Хранилище: то немногое от localStorage, что здесь нужно. */
export type DeviceStorage = Pick<Storage, 'getItem' | 'setItem'>

/** Источник случайности: `crypto` браузера, подменяемый в тестах. */
export type Randomness = Pick<Crypto, 'getRandomValues'>

/** Шестнадцать байт шестнадцатеричной строкой — как uuid, но без дефисов. */
export function newDeviceToken(random: Randomness): string {
  const bytes = random.getRandomValues(new Uint8Array(16))
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

/**
 * Токен этого устройства: сохранённый либо новый.
 *
 * Значение из хранилища проверяется схемой, а не берётся на веру:
 * мусор в хранилище (чужая версия, ручная правка) означал бы запрос
 * с невалидным заголовком, который сервер молча отбросит, — и драфт
 * стал бы недоступен без объяснения.
 */
export function deviceToken(storage: DeviceStorage, random: Randomness): string {
  try {
    const stored = storage.getItem(DEVICE_STORAGE_KEY)
    const parsed = deviceTokenSchema.safeParse(stored)
    if (parsed.success) return parsed.data
  } catch {
    // Хранилище недоступно — токен будет жить до перезагрузки.
  }

  const token = newDeviceToken(random)
  try {
    storage.setItem(DEVICE_STORAGE_KEY, token)
  } catch {
    // То же: драфт создастся, но не найдётся после перезагрузки.
  }
  return token
}
